import React, { useState, useEffect, useRef } from 'react';
import { TimeLog } from '../services/timeLogService';
import './TimeLogEditModal.css';

interface TimeLogEditModalProps {
  timeLog: TimeLog;
  onSave: (timeLog: TimeLog) => void;
  onDelete?: (id: string) => void;
  onClose: () => void;
}

const TimeLogEditModal: React.FC<TimeLogEditModalProps> = ({
  timeLog,
  onSave,
  onDelete,
  onClose
}) => {
  const [formData, setFormData] = useState<TimeLog>(timeLog);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus first input when modal opens
    firstInputRef.current?.focus();

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (formData.hours <= 0) {
      newErrors.hours = 'Hours must be greater than 0';
    }

    if (formData.hours > 24) {
      newErrors.hours = 'Hours cannot exceed 24';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  const handleTaskChange = (index: number, value: string) => {
    const newTasks = [...formData.tasks];
    newTasks[index] = value;
    setFormData({ ...formData, tasks: newTasks });
  };

  const addTask = () => {
    setFormData({ ...formData, tasks: [...formData.tasks, ''] });
  };

  const removeTask = (index: number) => {
    const newTasks = formData.tasks.filter((_, i) => i !== index);
    setFormData({ ...formData, tasks: newTasks });
  };

  const handleBusinessValueChange = (index: number, value: string) => {
    const newValues = [...formData.businessValue];
    newValues[index] = value;
    setFormData({ ...formData, businessValue: newValues });
  };

  const addBusinessValue = () => {
    setFormData({ ...formData, businessValue: [...formData.businessValue, ''] });
  };

  const removeBusinessValue = (index: number) => {
    const newValues = formData.businessValue.filter((_, i) => i !== index);
    setFormData({ ...formData, businessValue: newValues });
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date | string) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        <div className="modal-header">
          <h2 id="modal-title">
            {timeLog._id ? 'Edit Time Log Entry' : 'New Time Log Entry'}
          </h2>
          <button
            onClick={onClose}
            className="close-btn"
            aria-label="Close modal"
            type="button"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row-five">
            <div className="form-group">
              <label htmlFor="title">
                Title <span className="required" aria-label="required">*</span>
              </label>
              <input
                ref={firstInputRef}
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={errors.title ? 'error' : ''}
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? 'title-error' : undefined}
                placeholder="e.g., Landing Page Development"
                required
              />
              {errors.title && (
                <span id="title-error" className="error-message" role="alert">
                  {errors.title}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="date">
                Date <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="date"
                id="date"
                value={formatDateForInput(formData.date)}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className={errors.date ? 'error' : ''}
                aria-invalid={!!errors.date}
                aria-describedby={errors.date ? 'date-error' : undefined}
                required
              />
              {errors.date && (
                <span id="date-error" className="error-message" role="alert">
                  {errors.date}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="hours">
                Hours <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="number"
                id="hours"
                min="0"
                max="24"
                step="0.5"
                value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: parseFloat(e.target.value) || 0 })}
                className={errors.hours ? 'error' : ''}
                aria-invalid={!!errors.hours}
                aria-describedby={errors.hours ? 'hours-error' : undefined}
                required
              />
              {errors.hours && (
                <span id="hours-error" className="error-message" role="alert">
                  {errors.hours}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              >
                <option value="Planning">Planning</option>
                <option value="Development">Development</option>
                <option value="Testing">Testing</option>
                <option value="Documentation">Documentation</option>
                <option value="Learning">Learning</option>
                <option value="Bug Fixes">Bug Fixes</option>
                <option value="Refactoring">Refactoring</option>
                <option value="Performance">Performance</option>
                <option value="Security">Security</option>
                <option value="Deployment">Deployment</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <option value="completed">Completed</option>
                <option value="in-progress">In Progress</option>
                <option value="planned">Planned</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tasks Completed</label>
              {formData.tasks.map((task, index) => (
                <div key={index} className="array-input">
                  <input
                    type="text"
                    value={task}
                    onChange={(e) => handleTaskChange(index, e.target.value)}
                    placeholder="Describe a task..."
                    aria-label={`Task ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeTask(index)}
                    className="remove-btn"
                    aria-label={`Remove task ${index + 1}`}
                    disabled={formData.tasks.length === 1}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addTask}
                className="add-btn"
                aria-label="Add task"
              >
                + Add Task
              </button>
            </div>

            <div className="form-group">
              <label>Business Value</label>
              {formData.businessValue.map((value, index) => (
                <div key={index} className="array-input">
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => handleBusinessValueChange(index, e.target.value)}
                    placeholder="Describe business value..."
                    aria-label={`Business value ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeBusinessValue(index)}
                    className="remove-btn"
                    aria-label={`Remove business value ${index + 1}`}
                    disabled={formData.businessValue.length === 1}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addBusinessValue}
                className="add-btn"
                aria-label="Add business value"
              >
                + Add Business Value
              </button>
            </div>
          </div>

          <div className="modal-actions">
            <div>
              {onDelete && timeLog._id && (
                <button
                  type="button"
                  onClick={() => onDelete(timeLog._id!)}
                  className="delete-btn"
                  aria-label="Delete time log entry"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="action-buttons">
              <button
                type="button"
                onClick={onClose}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="save-btn"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimeLogEditModal;
